import { MouseEvent, useEffect, useState } from 'react';
import { Form } from 'antd';

import BaseSecuritiesModal from 'global/Securities/components/Modal/BaseSecuritiesModal';
import Image from 'global/Image';
import alertMessage from 'global/AlertMessage';
import type { BaseSecuritiesModalProps } from 'global/Securities/components/Modal/BaseSecuritiesModal';
import useMultiPortfolioStore from 'lib/stores/multiPorto';
import usePortfolioForm from 'features/multi-porto/hooks/usePortfolioCreation';

import {
  CharCount,
  Container,
  InputStyled,
  NextButton,
  InputError,
} from './rename.styled';

const RenamePortfolio = ({
  onCancel = () => {},
  onSubmit = () => {},
  ...props
}: BaseSecuritiesModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);

  const renameForm = useMultiPortfolioStore((s) => s.renamePortfolioForm);
  const postRenamePorto = useMultiPortfolioStore((s) => s.postRenamePorto);

  const {
    portfolioName,
    error,
    setPortfolioName,
    handlePortfolioNameChange,
    resetForm,
  } = usePortfolioForm();

  const handleReset = () => {
    resetForm();
    form.resetFields();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const payload = {
      ...renameForm,
      new_portfolio_name: portfolioName,
      idempotency_ref: `WEB-Rename-Porto-${Date.now()}`,
    };

    try {
      const result = await postRenamePorto(payload);
      if (typeof result === 'boolean' && result) {
        onSubmit();
        handleReset();
        alertMessage({
          content: 'Portfolio Name Has Been Changed',
          alertType: 'snackbar-dark',
          messageType: 'success',
          hasCloseIcon: true,
        });
      }
    } catch (err) {
      console.error('Error renaming portfolio:', err);
    }

    setLoading(false);
  };

  const handleCancel = (e: MouseEvent<HTMLElement>) => {
    onCancel(e);
    handleReset();
  };

  useEffect(() => {
    if (renameForm.new_portfolio_name) {
      setPortfolioName(renameForm.new_portfolio_name);
    }
  }, [renameForm.new_portfolio_name]);

  return (
    <BaseSecuritiesModal
      title="Rename Portfolio"
      hideSubmit
      onCancel={handleCancel}
      className="modal-multi-porto"
      transitionName=""
      {...props}
    >
      <Container>
        <Form form={form} layout="vertical">
          <Form.Item name="portfolio" label="Portfolio Name">
            <InputStyled
              value={portfolioName}
              onChange={handlePortfolioNameChange}
              maxLength={20}
              placeholder="Input Portfolio Name"
              suffix={
                <CharCount isErrored={!!error}>
                  {portfolioName.length}/20
                </CharCount>
              }
              isErrored={!!error}
            />

            {error && (
              <InputError>
                <Image src="/icon/alert/warning.svg" alt="" />
                <span>{error}</span>
              </InputError>
            )}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <NextButton
              onClick={handleSubmit}
              type="primary"
              disabled={
                !portfolioName ||
                !!error ||
                loading ||
                portfolioName === renameForm.new_portfolio_name
              }
              loading={loading}
            >
              Save
            </NextButton>
          </Form.Item>
        </Form>
      </Container>
    </BaseSecuritiesModal>
  );
};

export default RenamePortfolio;
